import { supabase } from './supabase/supabase';
import { getCurrentTenantId } from '@/lib/auth-utils';

export interface StoryContext {
  id: string;
  story_id: string;
  tenant_id: string;
  context_data: any;
  context_version: number;
  last_updated: string;
  created_at: string;
}

export interface StoryContextStatus {
  isInitialized: boolean;
  lastUpdated?: string;
  version?: number;
  isLoading: boolean;
  error?: string;
}

/**
 * Story Context Service
 * Handles initialization and management of story context for AI chat
 */
export const storyContextService = {
  /**
   * Check if story context is initialized
   */
  async checkContextStatus(storyId: string): Promise<StoryContextStatus> {
    try {
      const { data, error } = await supabase
        .from('story_contexts')
        .select('id, context_version, last_updated')
        .eq('story_id', storyId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        throw error;
      }

      return {
        isInitialized: !!data,
        lastUpdated: data?.last_updated,
        version: data?.context_version,
        isLoading: false,
      };
    } catch (error) {
      console.error('Error checking story context status:', error);
      return {
        isInitialized: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Initialize story context using the edge function
   */
  async initializeContext(
    storyId: string,
    projectId: string,
    forceRefresh: boolean = false
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Get session and tenant ID
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const tenantId = await getCurrentTenantId();
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      // Call the edge function - use local URL for development
      const response = await fetch(
        'http://127.0.0.1:54321/functions/v1/initialize-story-context',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'x-story-id': storyId,
            'x-project-id': projectId,
            'x-tenant-id': tenantId,
          },
          body: JSON.stringify({
            forceRefresh,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Context initialization failed: ${errorText}`);
      }

      await response.json();
      return { success: true };
    } catch (error) {
      console.error('Error initializing story context:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Get full story context data
   */
  async getContextData(storyId: string): Promise<StoryContext | null> {
    try {
      const { data, error } = await supabase
        .from('story_contexts')
        .select('*')
        .eq('story_id', storyId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data || null;
    } catch (error) {
      console.error('Error fetching story context data:', error);
      return null;
    }
  },

  /**
   * Delete story context (for re-initialization)
   */
  async deleteContext(storyId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('story_contexts')
        .delete()
        .eq('story_id', storyId);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting story context:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};
